import { Injectable } from '@angular/core';
import { BarcodeScanner, BarcodeFormat, GoogleBarcodeScannerModuleInstallState } from '@capacitor-mlkit/barcode-scanning';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class QrScannerService {
  private isSupported = false;
  private isModuleInstalling = false;

  constructor() {
    this.checkSupport();
  }

  /**
   * Check if barcode scanning is supported on this platform
   */
  private async checkSupport(): Promise<void> {
    const result = await BarcodeScanner.isSupported();
    this.isSupported = result.supported;
  }

  /**
   * Check if Google Barcode Scanner Module is available (Android only)
   * This module is required for the scan() method to work on Android
   */
  private async isGoogleModuleAvailable(): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'android') {
      return true; // Not needed on iOS/web
    }

    try {
      const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
      return available;
    } catch (error) {
      console.warn('[QrScanner] Could not check Google Barcode Scanner Module availability:', error);
      return false;
    }
  }

  /**
   * Install Google Barcode Scanner Module (Android only)
   * Returns a promise that resolves when installation is complete
   */
  private async installGoogleModule(): Promise<boolean> {
    if (this.isModuleInstalling) {
      return false;
    }

    this.isModuleInstalling = true;

    return new Promise<boolean>((resolve) => {
      let listenerHandle: { remove: () => Promise<void> } | null = null;

      const cleanup = async () => {
        this.isModuleInstalling = false;
        if (listenerHandle) {
          await listenerHandle.remove();
        }
      };

      // Set up listener for installation progress
      BarcodeScanner.addListener('googleBarcodeScannerModuleInstallProgress', async (event) => {

        switch (event.state) {
          case GoogleBarcodeScannerModuleInstallState.COMPLETED:
            await cleanup();
            resolve(true);
            break;

          case GoogleBarcodeScannerModuleInstallState.FAILED:
          case GoogleBarcodeScannerModuleInstallState.CANCELED:
            console.error('[QrScanner] Google Barcode Scanner Module installation failed or was canceled');
            await cleanup();
            resolve(false);
            break;
        }
      }).then(handle => {
        listenerHandle = handle;
      });

      // Start the installation
      BarcodeScanner.installGoogleBarcodeScannerModule().catch(async (error) => {
        console.error('[QrScanner] Failed to start module installation:', error);
        await cleanup();
        resolve(false);
      });

      // Timeout after 60 seconds
      setTimeout(async () => {
        if (this.isModuleInstalling) {
          console.warn('[QrScanner] Module installation timed out');
          await cleanup();
          resolve(false);
        }
      }, 60000);
    });
  }

  /**
   * Ensure Google Barcode Scanner Module is ready (Android only)
   * Checks availability and installs if necessary
   */
  private async ensureGoogleModuleReady(): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'android') {
      return true;
    }

    const isAvailable = await this.isGoogleModuleAvailable();
    if (isAvailable) {
      return true;
    }

    return await this.installGoogleModule();
  }

  /**
   * Check if camera permission is granted
   */
  async checkPermissions(): Promise<boolean> {
    try {
      const { camera } = await BarcodeScanner.checkPermissions();
      return camera === 'granted' || camera === 'limited';
    } catch (error) {
      console.error('[QrScanner] Permission check failed:', error);
      return false;
    }
  }

  /**
   * Request camera permission
   */
  async requestPermissions(): Promise<boolean> {
    try {
      const { camera } = await BarcodeScanner.requestPermissions();
      return camera === 'granted' || camera === 'limited';
    } catch (error) {
      console.error('[QrScanner] Permission request failed:', error);
      return false;
    }
  }

  /**
   * Start QR code scanning
   * Returns the scanned code or null if cancelled
   */
  async scanQrCode(): Promise<string | null> {
    try {
      // Check if supported
      if (!this.isSupported && !Capacitor.isNativePlatform()) {
        // Web platform - try to use web scanner
        console.warn('[QrScanner] Barcode scanning not supported on web, falling back to manual input');
        return null;
      }

      // Check permissions
      const hasPermission = await this.checkPermissions();
      if (!hasPermission) {
        const granted = await this.requestPermissions();
        if (!granted) {
          console.warn('[QrScanner] Camera permission denied');
          return null;
        }
      }

      // Ensure Google Barcode Scanner Module is installed (Android only)
      const moduleReady = await this.ensureGoogleModuleReady();
      if (!moduleReady) {
        console.error('[QrScanner] Google Barcode Scanner Module could not be installed');
        return null;
      }

      // Use scan() method which handles the entire scan lifecycle
      const { barcodes } = await BarcodeScanner.scan({
        formats: [BarcodeFormat.QrCode] // Only scan QR codes
      });

      if (barcodes && barcodes.length > 0) {
        const scannedValue = barcodes[0].rawValue;

        // Extract pairing code from URL if needed
        // URL format: https://app.fixplaner.com/terminal-pairing/pairing?code=ABCD1234
        const codeMatch = scannedValue.match(/[?&]code=([A-Z0-9]{8})/i);
        if (codeMatch) {
          return codeMatch[1].toUpperCase();
        }

        // If it's just the code (8 characters)
        if (/^[A-Z0-9]{8}$/i.test(scannedValue)) {
          return scannedValue.toUpperCase();
        }

        return scannedValue;
      }

      return null;
    } catch (error) {
      console.error('[QrScanner] Scan failed:', error);
      await this.stopScanning();
      return null;
    }
  }

  /**
   * Stop scanning (cleanup)
   */
  async stopScanning(): Promise<void> {
    try {
      await BarcodeScanner.stopScan();
    } catch (error) {
      console.error('[QrScanner] Stop scan failed:', error);
    }
  }

  /**
   * Check if scanning is supported on this device/platform
   */
  async isScanningSupported(): Promise<boolean> {
    try {
      const result = await BarcodeScanner.isSupported();
      return result.supported;
    } catch (error) {
      return false;
    }
  }

  /**
   * Open app settings (for permission management)
   */
  async openSettings(): Promise<void> {
    try {
      await BarcodeScanner.openSettings();
    } catch (error) {
      console.error('[QrScanner] Open settings failed:', error);
    }
  }
}
